/**
 * Service for managing section selection persistence in localStorage
 * Handles storage operations for section data and selection state
 */
class SectionSelectionStorage {
  constructor() {
    this.sectionsKeyPrefix = 'sectionSelections_'
    this.sectionDataKeyPrefix = 'sectionData_'
  }

  /**
   * Generate storage key for section selections
   * @param {string} documentId - The document ID
   * @param {string} templateName - The template name
   * @returns {string} The localStorage key
   */
  getSelectionsKey(documentId, templateName) {
    if (!documentId || !templateName) {
      console.warn('SectionSelectionStorage: Missing documentId or templateName')
      return null
    }
    // Use both document ID and template name for unique key
    return `${this.sectionsKeyPrefix}${documentId}_${templateName}`
  }

  /**
   * Generate storage key for section data
   * @param {string} documentId - The document ID
   * @param {string} templateName - The template name
   * @returns {string} The localStorage key
   */
  getSectionDataKey(documentId, templateName) {
    if (!documentId || !templateName) {
      console.warn('SectionSelectionStorage: No documentId or templateName provided')
      return null
    }
    return `${this.sectionDataKeyPrefix}${documentId}_${templateName}`
  }

  /**
   * Save selected section IDs to localStorage
   * @param {string} documentId - The document ID
   * @param {string} templateName - The template name
   * @param {Array} selectedSectionIds - Array of selected section IDs
   * @returns {boolean} Success status
   */
  saveSelections(documentId, templateName, selectedSectionIds) {
    try {
      const key = this.getSelectionsKey(documentId, templateName)
      if (!key) return false

      const dataToStore = {
        documentId,
        templateName,
        selectedSectionIds,
        savedAt: new Date().toISOString(),
        version: '1.0'
      }

      localStorage.setItem(key, JSON.stringify(dataToStore))
      console.log(`SectionSelectionStorage: Saved ${selectedSectionIds.length} selections for document ${documentId}, template ${templateName}`)
      return true
    } catch (error) {
      console.error('SectionSelectionStorage: Error saving selections:', error)
      return false
    }
  }

  /**
   * Load selected section IDs from localStorage
   * @param {string} documentId - The document ID
   * @param {string} templateName - The template name
   * @returns {Array|null} Array of selected section IDs or null if not found
   */
  loadSelections(documentId, templateName) {
    try {
      const key = this.getSelectionsKey(documentId, templateName)
      if (!key) return null

      const storedData = localStorage.getItem(key)
      if (!storedData) {
        console.log(`SectionSelectionStorage: No saved selections for document ${documentId}, template ${templateName}`)
        return null
      }

      const parsedData = JSON.parse(storedData)
      console.log(`SectionSelectionStorage: Loaded ${parsedData.selectedSectionIds.length} selections for document ${documentId}`)
      
      return parsedData.selectedSectionIds || []
    } catch (error) {
      console.error('SectionSelectionStorage: Error loading selections:', error)
      return null
    }
  }

  /**
   * Save section data (template sections) to localStorage
   * @param {string} documentId - The document ID
   * @param {string} templateName - The template name
   * @param {Array} sections - Array of section objects
   * @returns {boolean} Success status
   */
  saveSectionData(documentId, templateName, sections) {
    try {
      if (!documentId || !templateName) return false
      const key = this.getSectionDataKey(documentId, templateName)
      
      const dataToStore = {
        documentId,
        templateName,
        sections,
        savedAt: new Date().toISOString(),
        version: '1.0'
      }
      
      localStorage.setItem(key, JSON.stringify(dataToStore))
      console.log(`SectionSelectionStorage: Saved ${sections.length} sections for template ${templateName}`)
      return true
    } catch (error) {
      console.error('SectionSelectionStorage: Error saving section data:', error)
      return false
    }
  }

  /**
   * Load section data from localStorage
   * @param {string} documentId - The document ID
   * @param {string} templateName - The template name
   * @returns {Object|null} Object with sections array and metadata, or null
   */
  loadSectionData(documentId, templateName) {
    try {
      if (!documentId || !templateName) return null
      const key = this.getSectionDataKey(documentId, templateName)
      
      const storedData = localStorage.getItem(key)
      if (!storedData) {
        console.log(`SectionSelectionStorage: No saved section data for template ${templateName}`)
        return null
      }
      
      const parsedData = JSON.parse(storedData)
      
      // Check if data is older than 24 hours (sections shouldn't change often)
      const savedTime = new Date(parsedData.savedAt)
      const now = new Date()
      const diffHours = (now - savedTime) / (1000 * 60 * 60)
      
      if (diffHours > 24) {
        console.log(`SectionSelectionStorage: Section data for ${templateName} is older than 24 hours, will refresh`)
        return null
      }
      
      console.log(`SectionSelectionStorage: Loaded ${parsedData.sections.length} sections for template ${templateName}`)
      return {
        sections: parsedData.sections || [],
        savedAt: parsedData.savedAt
      }
    } catch (error) {
      console.error('SectionSelectionStorage: Error loading section data:', error)
      return null
    }
  }

  /**
   * Clear stored selections for a specific document
   * @param {string} documentId - The document ID
   * @param {string} templateName - The template name
   * @returns {boolean} Success status
   */
  clearSelections(documentId, templateName) {
    try {
      const key = this.getSelectionsKey(documentId, templateName)
      if (key) {
        localStorage.removeItem(key)
        console.log(`SectionSelectionStorage: Cleared selections for document ${documentId}, template ${templateName}`)
      }
      return true
    } catch (error) {
      console.error('SectionSelectionStorage: Error clearing selections:', error)
      return false
    }
  }

  /**
   * Clear all stored data for a document (selections only, not section data)
   * @param {string} documentId - The document ID
   * @returns {boolean} Success status
   */
  clearDocumentData(documentId) {
    try {
      let clearedCount = 0
      const keysToRemove = []

      // Find all selection keys for this document
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.sectionsKeyPrefix) && key.includes(documentId)) {
          keysToRemove.push(key)
        }
      }

      // Remove found keys
      keysToRemove.forEach(key => {
        localStorage.removeItem(key)
        clearedCount++
      })

      console.log(`SectionSelectionStorage: Cleared ${clearedCount} selection entries for document ${documentId}`)
      return true
    } catch (error) {
      console.error('SectionSelectionStorage: Error clearing document data:', error)
      return false
    }
  }

  /**
   * Clear section data cache for a template
   * @param {string} templateName - The template name
   * @returns {boolean} Success status
   */
  clearSectionData(documentId, templateName) {
    try {
      const key = this.getSectionDataKey(documentId, templateName)
      if (key) {
        localStorage.removeItem(key)
        console.log(`SectionSelectionStorage: Cleared section data for template ${templateName}`)
      }
      return true
    } catch (error) {
      console.error('SectionSelectionStorage: Error clearing section data:', error)
      return false
    }
  }

  /**
   * Check if selections exist for a document
   * @param {string} documentId - The document ID
   * @param {string} templateName - The template name
   * @returns {boolean} True if selections exist
   */
  hasSelections(documentId, templateName) {
    const key = this.getSelectionsKey(documentId, templateName)
    return key ? localStorage.getItem(key) !== null : false
  }

  /**
   * Get metadata about stored selections
   * @param {string} documentId - The document ID
   * @param {string} templateName - The template name
   * @returns {Object|null} Metadata object or null
   */
  getMetadata(documentId, templateName) {
    try {
      const key = this.getSelectionsKey(documentId, templateName)
      if (!key) return null

      const storedData = localStorage.getItem(key)
      if (!storedData) return null

      const parsedData = JSON.parse(storedData)
      return {
        documentId: parsedData.documentId,
        templateName: parsedData.templateName,
        savedAt: parsedData.savedAt,
        selectionCount: parsedData.selectedSectionIds.length,
        version: parsedData.version
      }
    } catch (error) {
      console.error('SectionSelectionStorage: Error getting metadata:', error)
      return null
    }
  }

  /**
   * Clear all section selection data from localStorage
   * @param {string} documentId - The document ID
   * @returns {number} Number of cleared entries
   */
  clearAll(documentId) {
    let clearedCount = 0
    const keysToRemove = []

    // Find all keys with our prefixes
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith(this.sectionsKeyPrefix) || key.startsWith(this.sectionDataKeyPrefix)) && key.includes(documentId)) {
        keysToRemove.push(key)
      }
    }

    // Remove found keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
      clearedCount++
    })

    console.log(`SectionSelectionStorage: Cleared ${clearedCount} entries`)
    return clearedCount
  }

  /**
 * Clear all section selection data from localStorage
 * @returns {number} Number of cleared entries
 */
  clearAll() {
    let clearedCount = 0
    const keysToRemove = []

    // Find all keys with our prefixes
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith(this.sectionsKeyPrefix) || key.startsWith(this.sectionDataKeyPrefix))) {
        keysToRemove.push(key)
      }
    }

    // Remove found keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
      clearedCount++
    })

    console.log(`SectionSelectionStorage: Cleared ${clearedCount} entries`)
    return clearedCount
  }
}

// Export singleton instance
export const sectionSelectionStorage = new SectionSelectionStorage()